import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AppCard from '../components/AppCard';
import { getApps } from '../services/api';

// Orden de categorías (3+ apps primero, luego 2 apps, al final 1 app)
const categoryOrder = [
  'Asistentes Virtuales',
  'ITSM',
  'Analítica',
  'Automatización',
  'Calidad',
  'Gestión de Activos',
  'Gestión Administrativa',
  'Gestión Operativa',
  'Telefonía',
  'Gestión de Accesos',
];

const statusLabels = {
  production: 'Disponibles',
  development: 'En desarrollo',
  beta: 'Beta',
  deprecated: 'Deprecadas',
};

const Catalog = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const categories = useMemo(() => {
    const cats = [...new Set(apps.map(app => app.category))];
    return cats.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
  }, [apps]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    apps.forEach(app => {
      counts[app.category] = (counts[app.category] || 0) + 1;
    });
    return counts;
  }, [apps]);

  const statusSegments = useMemo(() => {
    const present = [...new Set(apps.map(app => app.status))];
    const segments = [{ key: 'all', label: 'Todas', count: apps.length }];
    ['production', 'development', 'beta', 'deprecated'].forEach(s => {
      if (present.includes(s)) {
        segments.push({
          key: s,
          label: statusLabels[s],
          count: apps.filter(a => a.status === s).length,
        });
      }
    });
    return segments;
  }, [apps]);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        setLoading(true);
        const data = await getApps();
        setApps(data);
      } catch (err) {
        setError('Error al cargar las aplicaciones');
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  const filteredApps = useMemo(() => {
    let result = apps;

    if (searchTerm) {
      result = result.filter(
        app =>
          app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.technologies?.some(tech =>
            tech.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (categoryFilter !== 'all') {
      result = result.filter(app => app.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      result = result.filter(app => app.status === statusFilter);
    }

    return result;
  }, [searchTerm, categoryFilter, statusFilter, apps]);

  // Lista plana ordenada por categoría (para la rejilla)
  const sortedApps = useMemo(() => {
    return [...filteredApps].sort((a, b) => {
      const indexA = categoryOrder.indexOf(a.category);
      const indexB = categoryOrder.indexOf(b.category);
      const catDiff = (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      if (catDiff !== 0) return catDiff;
      return a.name.localeCompare(b.name);
    });
  }, [filteredApps]);

  // Agrupación por categoría (solo para el PDF)
  const groupedApps = useMemo(() => {
    const groups = {};
    sortedApps.forEach(app => {
      if (!groups[app.category]) groups[app.category] = [];
      groups[app.category].push(app);
    });
    return groups;
  }, [sortedApps]);

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || statusFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStatusFilter('all');
  };

  const generatePDF = async () => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF();

    // Header
    doc.setFillColor(15, 98, 254);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Catálogo de Herramientas', 20, 22);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Team I+D - IBM Colombia', 20, 30);

    let yPos = 50;
    const pageHeight = 280;

    Object.entries(groupedApps).forEach(([category, categoryApps]) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }

      doc.setTextColor(15, 98, 254);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(category, 20, yPos);
      doc.setDrawColor(15, 98, 254);
      doc.line(20, yPos + 2, 190, yPos + 2);
      yPos += 12;

      categoryApps.forEach((app) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }

        doc.setTextColor(22, 22, 22);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        const appNameText = `• ${app.name}`;
        doc.text(appNameText, 25, yPos);

        const nameWidth = doc.getTextWidth(appNameText);
        const statusText = app.status === 'production' ? 'Disponible' : 'En Desarrollo';
        const statusColor = app.status === 'production' ? [36, 161, 72] : [241, 194, 27];
        const badgeX = 25 + nameWidth + 5;
        const badgeWidth = app.status === 'production' ? 20 : 28;
        doc.setFillColor(...statusColor);
        doc.roundedRect(badgeX, yPos - 4, badgeWidth, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(statusText, badgeX + 2, yPos);

        yPos += 6;

        doc.setTextColor(82, 82, 82);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(app.description, 160);
        doc.text(descLines.slice(0, 2), 30, yPos);
        yPos += descLines.slice(0, 2).length * 4 + 2;

        if (app.url && app.url !== '#') {
          doc.setTextColor(15, 98, 254);
          doc.setFontSize(8);
          doc.textWithLink(app.url, 30, yPos, { url: app.url });
          yPos += 6;
        }
        yPos += 4;
      });

      yPos += 5;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setTextColor(168, 168, 168);
      doc.setFontSize(8);
      doc.text(`Página ${i} de ${pageCount}`, 100, 290, { align: 'center' });
      doc.text(`Generado el ${new Date().toLocaleDateString('es-CO')}`, 20, 290);
    }

    doc.save('catalogo-herramientas-id.pdf');
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f4f4', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar sticky */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 48,
          zIndex: 100,
        }}
      >
        <Container maxWidth="xl">
          {/* Fila 1: título + búsqueda + estados */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pt: 1.75,
              pb: 1.25,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ mr: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', lineHeight: 1.2, color: '#161616' }}>
                Catálogo
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: '#8d8d8d',
                  fontFamily: '"IBM Plex Mono", monospace',
                  lineHeight: 1.4,
                }}
              >
                {apps.length} aplicaciones · Team I+D
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <TextField
              placeholder="Buscar por nombre, descripción o tecnología"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#6f6f6f' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  backgroundColor: '#f4f4f4',
                  fontSize: '0.82rem',
                  '& fieldset': { border: '1px solid transparent' },
                  '&:hover fieldset': { border: '1px solid #c6c6c6' },
                  '&.Mui-focused fieldset': { border: '1px solid #0f62fe' },
                },
              }}
            />

            {/* Switcher de estados */}
            <Box sx={{ display: 'flex', border: '1px solid #d8d8d8' }}>
              {statusSegments.map(seg => {
                const active = statusFilter === seg.key;
                return (
                  <Box
                    key={seg.key}
                    onClick={() => setStatusFilter(seg.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setStatusFilter(seg.key);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.7,
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      backgroundColor: active ? '#161616' : 'white',
                      color: active ? 'white' : '#525252',
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                      '&:not(:last-of-type)': { borderRight: '1px solid #d8d8d8' },
                      '&:hover': active ? {} : { backgroundColor: '#f4f4f4' },
                      '&:focus-visible': { outline: '2px solid #0f62fe', outlineOffset: '-2px' },
                    }}
                  >
                    {seg.label}{' '}
                    <Box component="span" sx={{ opacity: 0.6, fontFamily: '"IBM Plex Mono", monospace' }}>
                      {seg.count}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Fila 2: riel de categorías */}
          <Box sx={{ display: 'flex', gap: 0.75, pb: 1.5, flexWrap: 'wrap' }}>
            {[{ key: 'all', label: 'Todas las categorías', count: null }].concat(
              categories.map(cat => ({ key: cat, label: cat, count: categoryCounts[cat] }))
            ).map(({ key, label, count }) => {
              const active = categoryFilter === key;
              return (
                <Box
                  key={key}
                  onClick={() => setCategoryFilter(active && key !== 'all' ? 'all' : key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setCategoryFilter(active && key !== 'all' ? 'all' : key);
                    }
                  }}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    border: '1px solid',
                    borderColor: active ? '#0f62fe' : '#d8d8d8',
                    backgroundColor: active ? '#0f62fe' : 'white',
                    color: active ? 'white' : '#525252',
                    transition: 'all 0.15s ease',
                    '&:hover': active ? {} : { borderColor: '#0f62fe', color: '#0f62fe' },
                    '&:focus-visible': { outline: '2px solid #0f62fe', outlineOffset: '2px' },
                  }}
                >
                  {label}
                  {count !== null && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.6,
                        opacity: active ? 0.75 : 0.55,
                        fontFamily: '"IBM Plex Mono", monospace',
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Rejilla de aplicaciones */}
      <Container maxWidth="xl" sx={{ py: 2.5, flexGrow: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : sortedApps.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#161616', mb: 0.5 }}>
              No hay resultados
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6f6f6f', mb: 2 }}>
              Ninguna aplicación coincide con los filtros actuales.
            </Typography>
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 0,
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  borderColor: '#0f62fe',
                  color: '#0f62fe',
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 1.5,
            }}
          >
            {sortedApps.map(app => (
              <AppCard key={app.id} app={app} />
            ))}
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          py: 2.5,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button
          onClick={generatePDF}
          startIcon={<PictureAsPdfIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            color: '#8d8d8d',
            fontSize: '0.72rem',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 0,
            '&:hover': { backgroundColor: 'transparent', color: '#161616' },
          }}
        >
          Exportar PDF
        </Button>
        <Button
          component={Link}
          to="/admin"
          startIcon={<AdminPanelSettingsIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            color: '#8d8d8d',
            fontSize: '0.72rem',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 0,
            '&:hover': { backgroundColor: 'transparent', color: '#161616' },
          }}
        >
          Administrar
        </Button>
      </Box>
    </Box>
  );
};

export default Catalog;
